
import { Component } from '@theme/component';

import {
  trapFocus,
  removeTrapFocus
} from '@theme/focus';

import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd
} from '@theme/utilities';

/**
 * Zero Foxtrot / Horizon
 * Accessible Header Drawer
 *
 * Improvements:
 * - Synchronize native details and ARIA state
 * - Focus newly expanded submenu content
 * - Trap focus in the currently visible panel
 * - Restore focus on Back, Close, and Escape
 * - Prevent stale animation callbacks from
 *   moving focus into a closed submenu
 */

class HeaderDrawer extends Component {
  requiredRefs = [
    'details',
    'menuDrawer'
  ];

  #transitionSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'keyup',
      this.#onKeyUp
    );

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'keyup',
      this.#onKeyUp
    );

    removeTrapFocus();
  }

  /**
   * Escape closes the nearest expanded
   * menu and restores focus.
   */
  #onKeyUp = (event) => {
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented
    ) {
      return;
    }

    const details =
      this.#getDetailsElement(event);

    if (!details.open) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.#close(details);
  };

  get isOpen() {
    return this.refs.details.open;
  }

  /**
   * Find the nearest native details element.
   */
  #getDetailsElement(event) {
    if (
      !(event?.target instanceof Element)
    ) {
      return this.refs.details;
    }

    return (
      event.target.closest('details') ??
      this.refs.details
    );
  }

  /**
   * Toggle the main navigation.
   *
   * Prevent the browser's default summary
   * toggle so the native open state,
   * animation state, and ARIA state
   * change together.
   */
  toggle(event) {
    event?.preventDefault?.();

    if (this.isOpen) {
      this.close();
    } else {
      this.open(undefined, event);
    }
  }

  /**
   * Open the main menu or selected submenu.
   */
  open(target, event) {
    event?.preventDefault?.();

    const details =
      this.#getDetailsElement(event);

    const summary = details.querySelector(
      ':scope > summary'
    );

    if (!summary) {
      return;
    }

    const isMainMenu =
      details === this.refs.details;

    const sequence =
      ++this.#transitionSequence;

    // Synchronize real disclosure state
    // and the state announced to JAWS.
    details.open = true;

    summary.setAttribute(
      'aria-expanded',
      'true'
    );

    this.preventInitialAccordionAnimations(
      details
    );

    requestAnimationFrame(() => {
      if (
        sequence !==
        this.#transitionSequence
      ) {
        return;
      }

      details.classList.add(
        'menu-open'
      );

      if (!isMainMenu) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      // Allow visibility styles to update
      // before calculating focusable links.
      requestAnimationFrame(() => {
        if (
          sequence !==
          this.#transitionSequence ||
          !details.open
        ) {
          return;
        }

        const panel = isMainMenu
          ? this.refs.menuDrawer
          : details.querySelector(
              ':scope > .menu-drawer__submenu'
            );

        if (!(panel instanceof HTMLElement)) {
          return;
        }

        // Trap only the current visible
        // panel, not hidden parent menus.
        trapFocus(panel);

        let focusTarget;

        if (isMainMenu) {
          focusTarget =
            panel.querySelector(
              '.menu-drawer__close-button'
            ) ||
            panel.querySelector(
              '.menu-drawer__navigation a'
            );
        } else {
          focusTarget =
            panel.querySelector(
              '.menu-drawer__back-button'
            ) ||
            panel.querySelector(
              '.menu-drawer__title'
            ) ||
            panel.querySelector('a[href]');
        }

        if (
          focusTarget instanceof HTMLElement
        ) {
          focusTarget.focus({
            preventScroll: true
          });
        } else {
          panel.focus({
            preventScroll: true
          });
        }
      });
    });
  }

  /**
   * Back button closes the currently
   * expanded submenu.
   */
  back(event) {
    event?.preventDefault?.();

    this.#close(
      this.#getDetailsElement(event)
    );
  }

  /**
   * Close the full navigation drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the selected disclosure.
   */
  #close(details) {
    if (
      !(details instanceof HTMLDetailsElement)
    ) {
      return;
    }

    const summary = details.querySelector(
      ':scope > summary'
    );

    if (!summary) {
      return;
    }

    const isMainMenu =
      details === this.refs.details;

    const sequence =
      ++this.#transitionSequence;

    summary.setAttribute(
      'aria-expanded',
      'false'
    );

    details.classList.remove(
      'menu-open'
    );

    if (isMainMenu) {
      this.refs.menuDrawer.classList.remove(
        'menu-drawer--has-submenu-opened'
      );
    } else {
      const otherOpenSubmenus =
        this.refs.menuDrawer.querySelectorAll(
          'details.menu-open'
        );

      if (!otherOpenSubmenus.length) {
        this.refs.menuDrawer.classList.remove(
          'menu-drawer--has-submenu-opened'
        );
      }
    }

    const drawer =
      details.querySelector(
        ':scope > .menu-drawer, :scope > .menu-drawer__submenu'
      ) || details;

    onAnimationEnd(
      drawer,
      () => {
        if (
          sequence !==
          this.#transitionSequence
        ) {
          return;
        }

        reset(details);

        if (isMainMenu) {
          removeTrapFocus();

          // Reset expanded nested menus
          // so no hidden submenus remain open.
          const expanded = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          expanded.forEach(reset);

          // Return to the original
          // main-menu trigger.
          summary.focus({
            preventScroll: true
          });
        } else {
          // Restore focus management to
          // the parent navigation panel.
          const parentSubmenu =
            details.parentElement?.closest(
              '.menu-drawer__submenu'
            );

          const parentPanel =
            parentSubmenu ||
            this.refs.menuDrawer;

          trapFocus(parentPanel);

          // Return to the category that
          // originally opened this submenu.
          summary.focus({
            preventScroll: true
          });
        }
      },
      {
        subtree: false
      }
    );
  }

  /**
   * Preserve Horizon animation cleanup.
   */
  #setupAnimatedElementListeners() {
    const animatedElements =
      this.querySelectorAll(
        '.menu-drawer__animated-element'
      );

    animatedElements.forEach(
      (element) => {
        element.addEventListener(
          'animationend',
          removeWillChangeOnAnimationEnd
        );
      }
    );
  }

  /**
   * Preserve Horizon accordion animation
   * behavior.
   */
  preventInitialAccordionAnimations(details) {
    const content =
      details.querySelectorAll(
        'accordion-custom .details-content'
      );

    content.forEach(
      (element) => {
        if (element instanceof HTMLElement) {
          element.classList.add(
            'details-content--no-animation'
          );
        }
      }
    );

    setTimeout(() => {
      content.forEach(
        (element) => {
          if (element instanceof HTMLElement) {
            element.classList.remove(
              'details-content--no-animation'
            );
          }
        }
      );
    }, 100);
  }
}

if (
  !customElements.get('header-drawer')
) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

/**
 * Reset a native details disclosure.
 */
function reset(element) {
  element.classList.remove(
    'menu-open'
  );

  element.open = false;

  element
    .querySelector(':scope > summary')
    ?.setAttribute(
      'aria-expanded',
      'false'
    );
}
